import { forwardRef } from "react";
import { ButtonLink, ButtonLinkProps } from "../../../Button";
import {
  ToolbarItemAppearance,
  WithAriaLabelEnforcement,
} from "../../sharedTypes";
import { ToolbarItemWrapper } from "../ToolbarItemWrapper/ToolbarItemWrapper";
import { useOverFlowItemContext } from "../ToolbarOverflowMenu/ToolbarOverflowMenu";

import cx from "classnames";
import styles from "../../Toolbar.module.scss";
import { Tooltip } from "../../../Tooltip";

/**
 * Base props for the ToolbarButtonLink component
 * @extends Omit<ButtonLinkProps, "appearance" | "aria-label" | "children">
 */
type ToolbarButtonLinkBaseProps = Omit<
  ButtonLinkProps,
  "appearance" | "aria-label" | "children"
> & {
  /**
   * Visual appearance of the button link
   * @default ghost
   */
  appearance?: ToolbarItemAppearance;
};

/**
 * Props for the ToolbarButtonLink component
 * @extends WithAriaLabelEnforcement<ToolbarButtonLinkBaseProps>
 */
type ToolbarButtonLinkProps =
  WithAriaLabelEnforcement<ToolbarButtonLinkBaseProps>;

/**
 * ToolbarButtonLink component for link actions within a toolbar.
 *
 * Features:
 * - Link functionality with href support
 * - Designed specifically for toolbar integration
 * - Consistent styling with other toolbar items
 * - Automatic tooltip for icon-only links
 * - Overflow menu support
 * - Accessibility enforcement for aria-labels
 * - Small size optimized for toolbar layout
 * - Ghost and primary appearance options
 * - Proper anchor element semantics
 *
 * @example
 * <ToolbarButtonLink href="/help">
 *   Help
 * </ToolbarButtonLink>
 *
 * @example
 * <ToolbarButtonLink icon={ExternalLinkIcon} href="/docs" aria-label="Documentation" />
 */
const ToolbarButtonLink = forwardRef<HTMLAnchorElement, ToolbarButtonLinkProps>(
  (props, _ref) => {
    const { appearance = "ghost", className, children, ...rest } = props;

    // Checks if this component is rendered inside the overflow menu and fallbacks when the context is unavailable
    const { isInOverflowMenu = false } = useOverFlowItemContext?.() || {};

    const buttonLinkClassNames = cx(className, styles["toolbar-button-item"]);

    const item = children ? (
      <ButtonLink
        appearance={appearance}
        size="small"
        data-anv="toolbar-button-link"
        className={buttonLinkClassNames}
        {...rest}
      >
        {children}
      </ButtonLink>
    ) : (
      <Tooltip>
        <Tooltip.Trigger>
          <ButtonLink
            appearance={appearance}
            size="small"
            data-anv="toolbar-button-link"
            className={buttonLinkClassNames}
            {...rest}
          />
        </Tooltip.Trigger>
        <Tooltip.Content>{rest["aria-label"]}</Tooltip.Content>
      </Tooltip>
    );

    if (isInOverflowMenu) {
      return item;
    }

    return (
      <ToolbarItemWrapper item={{ itemProps: props, itemType: "buttonLink" }}>
        {item}
      </ToolbarItemWrapper>
    );
  },
);

ToolbarButtonLink.displayName = "ToolbarButtonLink";

export { type ToolbarButtonLinkProps, ToolbarButtonLink };
